import React, { useState, useMemo, useCallback } from 'react';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  IconButton,
  Chip,
  Collapse,
  LinearProgress,
  Button,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import ColumnHeaderFilterIcon from '../../common/ColumnHeaderFilterIcon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { TABLE_STYLES } from '../../../constants/tableStyles';
import { SEARCE_COLORS } from '../../../theme';
import { ColumnVisibilityMenu } from '../../table/ColumnVisibilityMenu';
import TableCsvExportButton from '../../table/TableCsvExportButton';
import { exportEmployeeTrainingsSummaryToCsv } from './employeeTrainingsSummaryCsvExport';
import type { EmployeeTrainingSummary, CertificationRecord, Tier1TrendRecord, CompetencyTrendRecord } from '../../../types/driInsights';
import { NumericRangeColumnFilterMenu, CheckboxColumnFilterMenu } from '../../dri-insights/ColumnFilterMenu';
import { renderRoundedNumberCell } from '../../common/numericTableCells';
import { computeTableTotals } from '../../../utils/tableTotals';
import { MuiTableTotalsFooterRow } from '../../table/TableTotalsFooterRow';

export type TrainingSummarySortColumn =
  | 'employee_email'
  | 'registration_status'
  | 'total_certifications'
  | 'total_skill_badges'
  | 'dri_tier'
  | 'dri_score_avg'
  | 'dri_score_highest'
  | null;

export interface EmployeeTrainingsSummaryTableProps {
  /** Raw training summary data; table owns filtering, sorting, pagination. */
  data: EmployeeTrainingSummary[];
  getCertificationsForEmployee: (employeeEmail: string) => CertificationRecord[];
  formatDate: (dateString: string | null | undefined) => string;
  /** Tier1 trend records for Tier1 products column (only rows with tier1_accomplished_date not null). */
  tier1TrendData?: Tier1TrendRecord[];
  /** Competency trend records for Competencies column (only rows with competency_achieved_date not null). */
  competencyTrendData?: CompetencyTrendRecord[];
  /** When true, omit the "Employee Summary" heading (e.g. under outer "Employee summary" tabs). */
  hideTitle?: boolean;
  /** While unregistered list is loading (merged table still shows registered rows). */
  isLoadingUnregistered?: boolean;
}

type TrainingSummaryFilterState = {
  text?: string;
  min?: number | null;
  max?: number | null;
  selectedValues?: string[];
};

const DRI_TIER_FILTER_OPTIONS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'] as const;

function parseDriTierFilterLabel(s: string): number | null {
  const m = /^Tier\s*(\d)$/i.exec(String(s).trim());
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return n >= 1 && n <= 4 ? n : null;
}

const TRAINING_SUMMARY_COLUMN_VISIBILITY_ITEMS: { id: string; label: string; hideable?: boolean }[] = [
  { id: 'employee_email', label: 'Employee email', hideable: false },
  { id: 'registration_status', label: 'Status', hideable: false },
  { id: 'total_certifications', label: 'Total certifications' },
  { id: 'total_skill_badges', label: 'Total skill badges' },
  { id: 'dri_tier', label: 'DRI tier' },
  { id: 'dri_score_avg', label: 'DRI score avg' },
  { id: 'dri_score_highest', label: 'DRI score highest' },
  { id: 'competencies', label: 'Competencies' },
  { id: 'tier1_products', label: 'Tier1 products' },
  { id: 'certifications_list', label: 'Certifications list' },
];

/**
 * Employee Summary (Trainings Summary) table with internal sort, filter, pagination, and expandable certifications list.
 */
const EmployeeTrainingsSummaryTable: React.FC<EmployeeTrainingsSummaryTableProps> = ({
  data,
  getCertificationsForEmployee,
  formatDate,
  tier1TrendData = [],
  competencyTrendData = [],
  hideTitle = false,
  isLoadingUnregistered = false,
}) => {
  const isRowRegistered = (row: EmployeeTrainingSummary) => row.is_registered !== false;
  const [trainingSummaryPage, setTrainingSummaryPage] = useState(0);
  const [trainingSummaryRowsPerPage, setTrainingSummaryRowsPerPage] = useState(10);
  const [trainingSummarySortColumn, setTrainingSummarySortColumn] = useState<TrainingSummarySortColumn>(null);
  const [trainingSummarySortDirection, setTrainingSummarySortDirection] = useState<'asc' | 'desc'>('asc');
  const [trainingSummaryFilters, setTrainingSummaryFilters] = useState<Record<string, TrainingSummaryFilterState>>({});
  const [trainingSummaryFilterAnchorEl, setTrainingSummaryFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const [trainingSummaryTempNumericFilters, setTrainingSummaryTempNumericFilters] = useState<
    Record<string, { min?: number | null; max?: number | null }>
  >({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // State for certifications_list checkbox filter
  const [certListTempSelectedValues, setCertListTempSelectedValues] = useState<string[]>([]);
  const [certListFilterSearchText, setCertListFilterSearchText] = useState('');

  // State for competencies and tier1_products checkbox filters
  const [competenciesTempSelectedValues, setCompetenciesTempSelectedValues] = useState<string[]>([]);
  const [competenciesFilterSearchText, setCompetenciesFilterSearchText] = useState('');
  const [tier1ProductsTempSelectedValues, setTier1ProductsTempSelectedValues] = useState<string[]>([]);
  const [tier1ProductsFilterSearchText, setTier1ProductsFilterSearchText] = useState('');
  const [registrationStatusTempSelectedValues, setRegistrationStatusTempSelectedValues] = useState<string[]>([]);
  const [registrationStatusFilterSearchText, setRegistrationStatusFilterSearchText] = useState('');
  const [employeeEmailTempSelectedValues, setEmployeeEmailTempSelectedValues] = useState<string[]>([]);
  const [employeeEmailFilterSearchText, setEmployeeEmailFilterSearchText] = useState('');
  const [driTierTempSelectedValues, setDriTierTempSelectedValues] = useState<string[]>([]);
  const [driTierFilterSearchText, setDriTierFilterSearchText] = useState('');

  const allUniqueEmployeeEmails = useMemo(() => {
    const set = new Set<string>();
    data.forEach((row) => {
      if (row.employee_email) set.add(row.employee_email);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  // Build unique certifications list from ALL employees
  const allUniqueCertifications = useMemo(() => {
    const certSet = new Set<string>();
    data.forEach((employee) => {
      const certs = getCertificationsForEmployee(employee.employee_email);
      certs.forEach((cert) => {
        if (cert.certification_name) certSet.add(cert.certification_name);
      });
    });
    return Array.from(certSet).sort();
  }, [data, getCertificationsForEmployee]);

  // Build unique competencies from trend data (competency_achieved_date not null)
  const allUniqueCompetencies = useMemo(() => {
    const set = new Set<string>();
    (competencyTrendData || []).forEach((r) => {
      if (r.competency_achieved_date != null && r.competency_achieved_date !== '' && r.competency) {
        set.add(r.competency);
      }
    });
    return Array.from(set).sort();
  }, [competencyTrendData]);

  // Build unique tier1 products from trend data (tier1_accomplished_date not null)
  const allUniqueTier1Products = useMemo(() => {
    const set = new Set<string>();
    (tier1TrendData || []).forEach((r) => {
      if (r.tier1_accomplished_date != null && r.tier1_accomplished_date !== '' && r.gcp_product) {
        set.add(r.gcp_product);
      }
    });
    return Array.from(set).sort();
  }, [tier1TrendData]);

  const sortedAndFilteredTrainingSummary = useMemo(() => {
    let result = [...data];
    Object.keys(trainingSummaryFilters).forEach((key) => {
      const filter = trainingSummaryFilters[key];
      if (key === 'employee_email' && filter.selectedValues && filter.selectedValues.length > 0) {
        const allowed = new Set(filter.selectedValues);
        result = result.filter((item) => allowed.has(item.employee_email));
      } else if (key === 'employee_email' && filter.text) {
        result = result.filter((item) =>
          item.employee_email.toLowerCase().includes(filter.text!.toLowerCase())
        );
      } else if (key === 'certifications_list' && filter.selectedValues && filter.selectedValues.length > 0) {
        result = result.filter((item) => {
          const certs = getCertificationsForEmployee(item.employee_email);
          return certs.some(
            (c) => c.certification_name && filter.selectedValues!.includes(c.certification_name)
          );
        });
      } else if (key === 'certifications_list' && filter.text && filter.text.trim()) {
        const q = filter.text.trim().toLowerCase();
        result = result.filter((item) => {
          const certs = getCertificationsForEmployee(item.employee_email);
          return certs.some(
            (c) =>
              (c.certification_name && c.certification_name.toLowerCase().includes(q)) ||
              (c.certification_provider && c.certification_provider.toLowerCase().includes(q))
          );
        });
      } else if (key === 'competencies' && filter.selectedValues && filter.selectedValues.length > 0) {
        const selected = new Set(filter.selectedValues);
        result = result.filter((item) => {
          const withDate = (competencyTrendData || []).filter(
            (r) => (r.email ?? '') === item.employee_email && r.competency_achieved_date != null && r.competency_achieved_date !== ''
          );
          const names = withDate.map((r) => r.competency).filter((c): c is string => Boolean(c));
          return names.some((name) => selected.has(name));
        });
      } else if (key === 'tier1_products' && filter.selectedValues && filter.selectedValues.length > 0) {
        const selected = new Set(filter.selectedValues);
        result = result.filter((item) => {
          const withDate = (tier1TrendData || []).filter(
            (r) => (r.email ?? '') === item.employee_email && r.tier1_accomplished_date != null && r.tier1_accomplished_date !== ''
          );
          const products = withDate.map((r) => r.gcp_product).filter((p): p is string => Boolean(p));
          return products.some((product) => selected.has(product));
        });
      } else if (key === 'registration_status' && filter.selectedValues && filter.selectedValues.length > 0) {
        const selected = new Set(filter.selectedValues);
        result = result.filter((item) => {
          const label = item.is_registered === false ? 'Unregistered' : 'Registered';
          return selected.has(label);
        });
      } else if (key === 'dri_tier' && filter.selectedValues && filter.selectedValues.length > 0) {
        const allowedTiers = new Set(
          filter.selectedValues.map(parseDriTierFilterLabel).filter((n): n is number => n !== null)
        );
        if (allowedTiers.size > 0) {
          result = result.filter(
            (item) => item.is_registered !== false && allowedTiers.has(item.dri_tier)
          );
        }
      } else if (filter.min !== undefined || filter.max !== undefined) {
        const driOnlyNumericKeys = ['dri_score_avg', 'dri_score_highest'];
        const getValue = (item: EmployeeTrainingSummary) => item[key as keyof EmployeeTrainingSummary] as number;
        if (filter.min !== undefined && filter.min !== null) {
          result = result.filter((item) => {
            if (driOnlyNumericKeys.includes(key) && item.is_registered === false) return false;
            return getValue(item) >= filter.min!;
          });
        }
        if (filter.max !== undefined && filter.max !== null) {
          result = result.filter((item) => {
            if (driOnlyNumericKeys.includes(key) && item.is_registered === false) return false;
            return getValue(item) <= filter.max!;
          });
        }
      }
    });
    if (trainingSummarySortColumn) {
      result.sort((a, b) => {
        if (trainingSummarySortColumn === 'registration_status') {
          const aLabel = a.is_registered === false ? 'Unregistered' : 'Registered';
          const bLabel = b.is_registered === false ? 'Unregistered' : 'Registered';
          if (aLabel < bLabel) return trainingSummarySortDirection === 'asc' ? -1 : 1;
          if (aLabel > bLabel) return trainingSummarySortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        const aValue = a[trainingSummarySortColumn!];
        const bValue = b[trainingSummarySortColumn!];
        if (aValue < bValue) return trainingSummarySortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return trainingSummarySortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [data, trainingSummaryFilters, trainingSummarySortColumn, trainingSummarySortDirection, getCertificationsForEmployee, competencyTrendData, tier1TrendData]);

  const paginatedTrainingSummary = useMemo(() => {
    const startIndex = trainingSummaryPage * trainingSummaryRowsPerPage;
    return sortedAndFilteredTrainingSummary.slice(
      startIndex,
      startIndex + trainingSummaryRowsPerPage
    );
  }, [sortedAndFilteredTrainingSummary, trainingSummaryPage, trainingSummaryRowsPerPage]);

  const handleSort = (column: TrainingSummarySortColumn) => {
    const isAsc = trainingSummarySortColumn === column && trainingSummarySortDirection === 'asc';
    setTrainingSummarySortDirection(isAsc ? 'desc' : 'asc');
    setTrainingSummarySortColumn(column);
    setTrainingSummaryPage(0);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setTrainingSummaryFilterAnchorEl({ ...trainingSummaryFilterAnchorEl, [column]: event.currentTarget });
  };

  const handleFilterClose = (column: string) => {
    setTrainingSummaryFilterAnchorEl({ ...trainingSummaryFilterAnchorEl, [column]: null });
  };

  const handleFilterApply = (column: string) => {
    if (column === 'certifications_list') {
      setTrainingSummaryFilters({
        ...trainingSummaryFilters,
        [column]: { selectedValues: certListTempSelectedValues.length > 0 ? certListTempSelectedValues : undefined },
      });
      setCertListTempSelectedValues([]);
      setCertListFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    if (column === 'competencies') {
      setTrainingSummaryFilters({
        ...trainingSummaryFilters,
        [column]: { selectedValues: competenciesTempSelectedValues.length > 0 ? competenciesTempSelectedValues : undefined },
      });
      setCompetenciesTempSelectedValues([]);
      setCompetenciesFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    if (column === 'tier1_products') {
      setTrainingSummaryFilters({
        ...trainingSummaryFilters,
        [column]: { selectedValues: tier1ProductsTempSelectedValues.length > 0 ? tier1ProductsTempSelectedValues : undefined },
      });
      setTier1ProductsTempSelectedValues([]);
      setTier1ProductsFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    if (column === 'registration_status') {
      setTrainingSummaryFilters({
        ...trainingSummaryFilters,
        [column]: {
          selectedValues:
            registrationStatusTempSelectedValues.length > 0 ? registrationStatusTempSelectedValues : undefined,
        },
      });
      setRegistrationStatusTempSelectedValues([]);
      setRegistrationStatusFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    if (column === 'employee_email') {
      const next = { ...trainingSummaryFilters };
      if (employeeEmailTempSelectedValues.length > 0) {
        next[column] = { selectedValues: employeeEmailTempSelectedValues };
      } else {
        delete next[column];
      }
      setTrainingSummaryFilters(next);
      setEmployeeEmailTempSelectedValues([]);
      setEmployeeEmailFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    if (column === 'dri_tier') {
      const next = { ...trainingSummaryFilters };
      if (driTierTempSelectedValues.length > 0) {
        next[column] = { selectedValues: driTierTempSelectedValues };
      } else {
        delete next[column];
      }
      setTrainingSummaryFilters(next);
      setDriTierTempSelectedValues([]);
      setDriTierFilterSearchText('');
      handleFilterClose(column);
      setTrainingSummaryPage(0);
      return;
    }
    setTrainingSummaryFilters({
      ...trainingSummaryFilters,
      [column]: trainingSummaryTempNumericFilters[column],
    });
    handleFilterClose(column);
    setTrainingSummaryPage(0);
  };

  const handleFilterClear = (column: string) => {
    const newFilters = { ...trainingSummaryFilters };
    delete newFilters[column];
    setTrainingSummaryFilters(newFilters);
    if (column === 'certifications_list') {
      setCertListTempSelectedValues([]);
      setCertListFilterSearchText('');
    } else if (column === 'competencies') {
      setCompetenciesTempSelectedValues([]);
      setCompetenciesFilterSearchText('');
    } else if (column === 'tier1_products') {
      setTier1ProductsTempSelectedValues([]);
      setTier1ProductsFilterSearchText('');
    } else if (column === 'registration_status') {
      setRegistrationStatusTempSelectedValues([]);
      setRegistrationStatusFilterSearchText('');
    } else if (column === 'employee_email') {
      setEmployeeEmailTempSelectedValues([]);
      setEmployeeEmailFilterSearchText('');
    } else if (column === 'dri_tier') {
      setDriTierTempSelectedValues([]);
      setDriTierFilterSearchText('');
    } else {
      const newNumericFilters = { ...trainingSummaryTempNumericFilters };
      delete newNumericFilters[column];
      setTrainingSummaryTempNumericFilters(newNumericFilters);
    }
    handleFilterClose(column);
    setTrainingSummaryPage(0);
  };

  const hasActiveFilter = (columnId: string) => {
    const f = trainingSummaryFilters[columnId];
    if (columnId === 'employee_email') {
      return !!(f?.selectedValues && f.selectedValues.length > 0) || !!(f?.text && f.text.trim());
    }
    if (
      columnId === 'certifications_list' ||
      columnId === 'competencies' ||
      columnId === 'tier1_products' ||
      columnId === 'registration_status' ||
      columnId === 'dri_tier'
    ) {
      return !!(f?.selectedValues && f.selectedValues.length > 0);
    }
    return f?.min !== undefined || f?.max !== undefined;
  };

  const renderFilterMenu = (column: string, label: string, isNumeric: boolean = false) => {
    const anchorEl = trainingSummaryFilterAnchorEl[column];
    const open = Boolean(anchorEl);
    const filter = trainingSummaryFilters[column];

    if (column === 'registration_status') {
      const options = ['Registered', 'Unregistered'];
      const currentApplied = filter?.selectedValues || [];
      const selectedValues =
        registrationStatusTempSelectedValues.length > 0 ? registrationStatusTempSelectedValues : currentApplied;
      const filteredOptions = options.filter((v) =>
        v.toLowerCase().includes(registrationStatusFilterSearchText.toLowerCase())
      );
      const hasFilterActive = !!(filter?.selectedValues && filter.selectedValues.length > 0);
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setRegistrationStatusTempSelectedValues([]);
            setRegistrationStatusFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setRegistrationStatusTempSelectedValues(next);
          }}
          searchText={registrationStatusFilterSearchText}
          onSearchTextChange={(v) => setRegistrationStatusFilterSearchText(v)}
          onSelectAll={() => setRegistrationStatusTempSelectedValues(filteredOptions)}
          onClearAll={() => setRegistrationStatusTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    // Competencies checkbox filter
    if (column === 'competencies') {
      const currentApplied = filter?.selectedValues || [];
      const selectedValues = competenciesTempSelectedValues.length > 0 ? competenciesTempSelectedValues : currentApplied;
      const filteredOptions = allUniqueCompetencies.filter((v) =>
        v.toLowerCase().includes(competenciesFilterSearchText.toLowerCase())
      );
      const hasFilterActive = !!(filter?.selectedValues && filter.selectedValues.length > 0);
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setCompetenciesTempSelectedValues([]);
            setCompetenciesFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setCompetenciesTempSelectedValues(next);
          }}
          searchText={competenciesFilterSearchText}
          onSearchTextChange={(v) => setCompetenciesFilterSearchText(v)}
          onSelectAll={() => setCompetenciesTempSelectedValues(filteredOptions)}
          onClearAll={() => setCompetenciesTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    // Tier1 products checkbox filter
    if (column === 'tier1_products') {
      const currentApplied = filter?.selectedValues || [];
      const selectedValues = tier1ProductsTempSelectedValues.length > 0 ? tier1ProductsTempSelectedValues : currentApplied;
      const filteredOptions = allUniqueTier1Products.filter((v) =>
        v.toLowerCase().includes(tier1ProductsFilterSearchText.toLowerCase())
      );
      const hasFilterActive = !!(filter?.selectedValues && filter.selectedValues.length > 0);
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setTier1ProductsTempSelectedValues([]);
            setTier1ProductsFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setTier1ProductsTempSelectedValues(next);
          }}
          searchText={tier1ProductsFilterSearchText}
          onSearchTextChange={(v) => setTier1ProductsFilterSearchText(v)}
          onSelectAll={() => setTier1ProductsTempSelectedValues(filteredOptions)}
          onClearAll={() => setTier1ProductsTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    // Certifications List uses a checkbox filter with all unique certifications from every employee
    if (column === 'certifications_list') {
      const currentApplied = filter?.selectedValues || [];
      const selectedValues = certListTempSelectedValues.length > 0 ? certListTempSelectedValues : currentApplied;
      const filteredOptions = allUniqueCertifications.filter((v) =>
        v.toLowerCase().includes(certListFilterSearchText.toLowerCase())
      );
      const hasFilterActive = !!(filter?.selectedValues && filter.selectedValues.length > 0);
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setCertListTempSelectedValues([]);
            setCertListFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setCertListTempSelectedValues(next);
          }}
          searchText={certListFilterSearchText}
          onSearchTextChange={(v) => setCertListFilterSearchText(v)}
          onSelectAll={() => setCertListTempSelectedValues(filteredOptions)}
          onClearAll={() => setCertListTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    if (column === 'employee_email') {
      const currentApplied = filter?.selectedValues || [];
      const selectedValues =
        employeeEmailTempSelectedValues.length > 0 ? employeeEmailTempSelectedValues : currentApplied;
      const filteredOptions = allUniqueEmployeeEmails.filter((v) =>
        v.toLowerCase().includes(employeeEmailFilterSearchText.toLowerCase())
      );
      const hasFilterActive =
        !!(filter?.selectedValues && filter.selectedValues.length > 0) || !!(filter?.text && filter.text.trim());
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setEmployeeEmailTempSelectedValues([]);
            setEmployeeEmailFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setEmployeeEmailTempSelectedValues(next);
          }}
          searchText={employeeEmailFilterSearchText}
          onSearchTextChange={(v) => setEmployeeEmailFilterSearchText(v)}
          onSelectAll={() => setEmployeeEmailTempSelectedValues(filteredOptions)}
          onClearAll={() => setEmployeeEmailTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    if (column === 'dri_tier') {
      const currentApplied = filter?.selectedValues || [];
      const selectedValues =
        driTierTempSelectedValues.length > 0 ? driTierTempSelectedValues : currentApplied;
      const filteredOptions = [...DRI_TIER_FILTER_OPTIONS].filter((v) =>
        v.toLowerCase().includes(driTierFilterSearchText.toLowerCase())
      );
      const hasFilterActive = !!(filter?.selectedValues && filter.selectedValues.length > 0);
      return (
        <CheckboxColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => {
            handleFilterClose(column);
            setDriTierTempSelectedValues([]);
            setDriTierFilterSearchText('');
          }}
          columnLabel={label}
          options={filteredOptions}
          selectedValues={selectedValues}
          onToggle={(value) => {
            const base = selectedValues;
            const next = base.includes(value) ? base.filter((v) => v !== value) : [...base, value];
            setDriTierTempSelectedValues(next);
          }}
          searchText={driTierFilterSearchText}
          onSearchTextChange={(v) => setDriTierFilterSearchText(v)}
          onSelectAll={() => setDriTierTempSelectedValues([...filteredOptions])}
          onClearAll={() => setDriTierTempSelectedValues([])}
          onApply={() => handleFilterApply(column)}
          hasFilter={hasFilterActive}
          onClearFilter={() => handleFilterClear(column)}
        />
      );
    }

    if (isNumeric) {
      const temp = trainingSummaryTempNumericFilters[column];
      const min = temp?.min !== undefined ? temp.min : (filter?.min !== undefined ? filter.min : null);
      const max = temp?.max !== undefined ? temp.max : (filter?.max !== undefined ? filter.max : null);
      const hasFilter = filter?.min !== undefined || filter?.max !== undefined;
      return (
        <NumericRangeColumnFilterMenu
          open={open}
          anchorEl={anchorEl}
          onClose={() => handleFilterClose(column)}
          columnLabel={label}
          min={min}
          max={max}
          onMinChange={(v) =>
            setTrainingSummaryTempNumericFilters({
              ...trainingSummaryTempNumericFilters,
              [column]: { ...trainingSummaryTempNumericFilters[column], min: v },
            })
          }
          onMaxChange={(v) =>
            setTrainingSummaryTempNumericFilters({
              ...trainingSummaryTempNumericFilters,
              [column]: { ...trainingSummaryTempNumericFilters[column], max: v },
            })
          }
          onApply={() => handleFilterApply(column)}
          onClear={() => handleFilterClear(column)}
          hasFilter={!!hasFilter}
        />
      );
    }

  };

  const handleExpandToggle = (employeeEmail: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(employeeEmail)) next.delete(employeeEmail);
      else next.add(employeeEmail);
      return next;
    });
  };

  const columns: { id: string; label: string; minWidth: number; numeric?: boolean }[] = [
    { id: 'employee_email', label: 'Employee Email', minWidth: 200 },
    { id: 'registration_status', label: 'Status', minWidth: 130, numeric: false },
    { id: 'total_certifications', label: 'Total Certifications', minWidth: 150, numeric: true },
    { id: 'total_skill_badges', label: 'Total Skill Badges', minWidth: 150, numeric: true },
    { id: 'dri_tier', label: 'DRI Tier', minWidth: 100, numeric: false },
    { id: 'dri_score_avg', label: 'DRI Score Avg', minWidth: 120, numeric: true },
    { id: 'dri_score_highest', label: 'DRI Score Highest', minWidth: 150, numeric: true },
    { id: 'competencies', label: 'Competencies', minWidth: 140, numeric: false },
    { id: 'tier1_products', label: 'Tier1 products', minWidth: 100, numeric: false },
    { id: 'certifications_list', label: 'Certifications List', minWidth: 150, numeric: false },
  ];

  const visibleColumns = useMemo(
    () => columns.filter((c) => columnVisibility[c.id] !== false),
    [columns, columnVisibility]
  );

  const trainingFooterTotals = useMemo(() => {
    if (!sortedAndFilteredTrainingSummary.length) return null;
    return computeTableTotals(
      'dri-employee-trainings-summary',
      sortedAndFilteredTrainingSummary as unknown as Record<string, unknown>[],
      visibleColumns.map((c) => c.id),
    );
  }, [sortedAndFilteredTrainingSummary, visibleColumns]);

  const onColumnVisibilityChange = useCallback<OnChangeFn<VisibilityState>>((updater) => {
    setColumnVisibility((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const hasAnyTrainingSummaryFiltersApplied = useMemo(() => {
    return Object.keys(trainingSummaryFilters).some((key) => {
      const f = trainingSummaryFilters[key];
      if (!f) return false;
      if (
        key === 'certifications_list' ||
        key === 'competencies' ||
        key === 'tier1_products' ||
        key === 'registration_status' ||
        key === 'dri_tier'
      ) {
        if (f.selectedValues && f.selectedValues.length > 0) return true;
        if (key === 'certifications_list' && f.text && f.text.trim()) return true;
        return false;
      }
      if (key === 'employee_email') {
        if (f.selectedValues && f.selectedValues.length > 0) return true;
        return !!(f.text && f.text.trim());
      }
      return f.min !== undefined || f.max !== undefined;
    });
  }, [trainingSummaryFilters]);

  const handleClearAllTrainingSummaryFilters = useCallback(() => {
    setTrainingSummaryFilters({});
    setTrainingSummaryTempNumericFilters({});
    setCertListTempSelectedValues([]);
    setCertListFilterSearchText('');
    setCompetenciesTempSelectedValues([]);
    setCompetenciesFilterSearchText('');
    setTier1ProductsTempSelectedValues([]);
    setTier1ProductsFilterSearchText('');
    setRegistrationStatusTempSelectedValues([]);
    setRegistrationStatusFilterSearchText('');
    setEmployeeEmailTempSelectedValues([]);
    setEmployeeEmailFilterSearchText('');
    setDriTierTempSelectedValues([]);
    setDriTierFilterSearchText('');
    setTrainingSummaryFilterAnchorEl({});
    setTrainingSummaryPage(0);
  }, []);

  const clearFiltersButtonSx = {
    color: SEARCE_COLORS.primary,
    borderColor: SEARCE_COLORS.primary,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
      borderColor: SEARCE_COLORS.primary,
    },
  } as const;

  return (
    <>
      {!hideTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
            Employee Summary
          </Typography>
        </Box>
      )}

      {isLoadingUnregistered ? <LinearProgress sx={{ mb: 0.5 }} /> : null}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 1,
          px: 2,
          ...TABLE_STYLES.tableToolbarTop,
        }}
      >
        {hasAnyTrainingSummaryFiltersApplied && (
          <Button variant="outlined" size="small" onClick={handleClearAllTrainingSummaryFilters} sx={clearFiltersButtonSx}>
            Clear filters
          </Button>
        )}
        <TableCsvExportButton
          disabled={sortedAndFilteredTrainingSummary.length === 0}
          onClick={() =>
            exportEmployeeTrainingsSummaryToCsv({
              rows: sortedAndFilteredTrainingSummary,
              columnVisibility,
              getCertificationsForEmployee,
              tier1TrendData,
              competencyTrendData,
            })
          }
        />
        <ColumnVisibilityMenu
          items={TRAINING_SUMMARY_COLUMN_VISIBILITY_ITEMS}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={onColumnVisibilityChange}
          triggerLabel="Toggle Columns"
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          ...TABLE_STYLES.tableContainer,
          maxHeight: 450,
          mb: hideTitle ? 0 : 4,
          mt: hideTitle ? 0 : undefined,
          border: `1px solid ${SEARCE_COLORS.neutral_200}`,
        }}
      >
        <Table size={TABLE_STYLES.tableSize} stickyHeader aria-label="employee trainings summary table">
          <TableHead>
            <TableRow sx={{ backgroundColor: SEARCE_COLORS.primary }}>
              {visibleColumns.map((column) => {
                const noSortColumn =
                  column.id === 'certifications_list' ||
                  column.id === 'competencies' ||
                  column.id === 'tier1_products';
                return (
                <TableCell
                  key={column.id}
                  align={column.id === 'employee_email' ? 'left' : 'center'}
                  sx={{
                    ...TABLE_STYLES.cellHeader,
                    backgroundColor: SEARCE_COLORS.primary,
                    fontWeight: 600,
                    color: SEARCE_COLORS.white,
                    minWidth: column.minWidth,
                    '&:hover': {
                      backgroundColor:
                        noSortColumn
                          ? undefined
                          : darken(SEARCE_COLORS.primary, 0.1),
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent:
                        column.id === 'employee_email' ? 'flex-start' : 'center',
                      gap: 0.5,
                    }}
                  >
                    {noSortColumn ? (
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        {column.label}
                      </Box>
                    ) : (
                      <Box
                        component="button"
                        type="button"
                        onClick={() => handleSort(column.id as TrainingSummarySortColumn)}
                        onMouseDown={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          margin: 0,
                          padding: 0,
                          border: 'none',
                          background: 'none',
                          color: 'inherit',
                          font: 'inherit',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textAlign: 'inherit',
                          flex: column.id === 'employee_email' ? '1 1 auto' : undefined,
                          minWidth: 0,
                          '&:hover': { opacity: 0.92 },
                        }}
                      >
                        {column.label}
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterClick(e, column.id);
                      }}
                      sx={{
                        color: hasActiveFilter(column.id) ? SEARCE_COLORS.white : 'rgba(255, 255, 255, 0.7)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        padding: '4px',
                      }}
                    >
                      <ColumnHeaderFilterIcon isFilterActive={hasActiveFilter(column.id)} fontSize="small" />
                    </IconButton>
                  </Box>
                  {renderFilterMenu(column.id, column.label, (column as { numeric?: boolean }).numeric)}
                </TableCell>
              );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTrainingSummary.length > 0 ? (
              paginatedTrainingSummary.map((row, index) => {
                const rowKey = `${row.employee_email}-${index}`;
                const isExpanded = expandedRows.has(row.employee_email);
                const employeeCertifications = getCertificationsForEmployee(row.employee_email);

                return (
                  <React.Fragment key={rowKey}>
                    <TableRow sx={TABLE_STYLES.tableRowHover}>
                      {visibleColumns.map((column) => {
                        if (column.id === 'employee_email') {
                          return (
                            <TableCell key={column.id} sx={{ ...TABLE_STYLES.cell, fontWeight: 500 }}>
                              {row.employee_email}
                            </TableCell>
                          );
                        }
                        if (column.id === 'registration_status') {
                          const registered = isRowRegistered(row);
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              <Chip
                                label={registered ? 'Registered' : 'Unregistered'}
                                size="small"
                                sx={{
                                  ...TABLE_STYLES.chipInCell,
                                  backgroundColor: registered ? `${SEARCE_COLORS.success}18` : `${SEARCE_COLORS.neutral_400}22`,
                                  color: registered ? SEARCE_COLORS.success : SEARCE_COLORS.textSecondary,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          );
                        }
                        if (column.id === 'total_certifications') {
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              {row.total_certifications}
                            </TableCell>
                          );
                        }
                        if (column.id === 'total_skill_badges') {
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              {row.total_skill_badges}
                            </TableCell>
                          );
                        }
                        if (column.id === 'dri_tier') {
                          if (!isRowRegistered(row)) {
                            return (
                              <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              <Chip
                                label={`Tier ${row.dri_tier}`}
                                size="small"
                                sx={{
                                  ...TABLE_STYLES.chipInCell,
                                  backgroundColor:
                                    row.dri_tier === 1
                                      ? `${SEARCE_COLORS.success}15`
                                      : row.dri_tier === 2
                                        ? `${SEARCE_COLORS.primary}15`
                                        : row.dri_tier === 3
                                          ? '#FFC10715'
                                          : `${SEARCE_COLORS.error}15`,
                                  color:
                                    row.dri_tier === 1
                                      ? SEARCE_COLORS.success
                                      : row.dri_tier === 2
                                        ? SEARCE_COLORS.primary
                                        : row.dri_tier === 3
                                          ? '#FFC107'
                                          : SEARCE_COLORS.error,
                                  fontWeight: 600,
                                }}
                              />
                            </TableCell>
                          );
                        }
                        if (column.id === 'dri_score_avg') {
                          if (!isRowRegistered(row)) {
                            return (
                              <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              {renderRoundedNumberCell(row.dri_score_avg, 2)}
                            </TableCell>
                          );
                        }
                        if (column.id === 'dri_score_highest') {
                          if (!isRowRegistered(row)) {
                            return (
                              <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              {renderRoundedNumberCell(row.dri_score_highest, 2)}
                            </TableCell>
                          );
                        }
                        if (column.id === 'competencies') {
                          if (!isRowRegistered(row)) {
                            return (
                              <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell
                              key={column.id}
                              align="center"
                              sx={{
                                ...TABLE_STYLES.cell,
                                maxWidth: 200,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {(() => {
                                const withDate = (competencyTrendData || []).filter(
                                  (r) =>
                                    (r.email ?? '') === row.employee_email &&
                                    r.competency_achieved_date != null &&
                                    r.competency_achieved_date !== ''
                                );
                                const names = Array.from(
                                  new Set(withDate.map((r) => r.competency).filter((c): c is string => Boolean(c)))
                                );
                                return names.length > 0 ? names.join(', ') : '—';
                              })()}
                            </TableCell>
                          );
                        }
                        if (column.id === 'tier1_products') {
                          if (!isRowRegistered(row)) {
                            return (
                              <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                                -
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell
                              key={column.id}
                              align="center"
                              sx={{
                                ...TABLE_STYLES.cell,
                                maxWidth: 200,
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {(() => {
                                const withDate = (tier1TrendData || []).filter(
                                  (r) =>
                                    (r.email ?? '') === row.employee_email &&
                                    r.tier1_accomplished_date != null &&
                                    r.tier1_accomplished_date !== ''
                                );
                                const products = Array.from(
                                  new Set(withDate.map((r) => r.gcp_product).filter((p): p is string => Boolean(p)))
                                );
                                return products.length > 0 ? products.join(', ') : '—';
                              })()}
                            </TableCell>
                          );
                        }
                        if (column.id === 'certifications_list') {
                          return (
                            <TableCell key={column.id} align="center" sx={TABLE_STYLES.cell}>
                              <IconButton
                                aria-label="expand certifications"
                                size="small"
                                onClick={() => handleExpandToggle(row.employee_email)}
                                disabled={employeeCertifications.length === 0}
                                sx={{
                                  ...TABLE_STYLES.iconButtonInCell,
                                  color:
                                    employeeCertifications.length > 0 ? SEARCE_COLORS.primary : SEARCE_COLORS.neutral_300,
                                  '&:hover': {
                                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                                  },
                                }}
                              >
                                {isExpanded ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={Math.max(1, visibleColumns.length)}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: '4px 8px 8px' }}>
                              <Typography
                                variant="h6"
                                gutterBottom
                                component="div"
                                sx={{
                                  fontSize: '0.8rem',
                                  fontWeight: 600,
                                  color: SEARCE_COLORS.textPrimary,
                                  mb: 1,
                                }}
                              >
                                Certifications List
                              </Typography>
                              {employeeCertifications.length > 0 ? (
                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                    gap: 0.75,
                                  }}
                                >
                                  {employeeCertifications.map((cert, certIndex) => (
                                    <Box
                                      key={`${cert.certification_name}-${certIndex}`}
                                      sx={{
                                        px: 1,
                                        py: 0.75,
                                        border: `1px solid ${SEARCE_COLORS.neutral_200}`,
                                        borderRadius: 1,
                                        backgroundColor: SEARCE_COLORS.white,
                                        '&:hover': { backgroundColor: alpha(SEARCE_COLORS.primary, 0.03) },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          gap: 0.5,
                                          mb: 0.25,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            fontWeight: 600,
                                            color: SEARCE_COLORS.textPrimary,
                                            fontSize: '0.75rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                          }}
                                        >
                                          {cert.certification_name}
                                        </Typography>
                                        {cert.status && (
                                          <Chip
                                            label={cert.status}
                                            size="small"
                                            sx={{
                                              flexShrink: 0,
                                              backgroundColor:
                                                cert.status === 'Active'
                                                  ? `${SEARCE_COLORS.success}15`
                                                  : cert.status === 'Expiring Soon'
                                                    ? `${SEARCE_COLORS.warning}15`
                                                    : `${SEARCE_COLORS.error}15`,
                                              color:
                                                cert.status === 'Active'
                                                  ? SEARCE_COLORS.success
                                                  : cert.status === 'Expiring Soon'
                                                    ? SEARCE_COLORS.warning
                                                    : SEARCE_COLORS.error,
                                              fontWeight: 600,
                                              fontSize: '0.6rem',
                                              height: '18px',
                                            }}
                                          />
                                        )}
                                      </Box>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          gap: 0.5,
                                          alignItems: 'center',
                                          fontSize: '0.7rem',
                                          color: SEARCE_COLORS.textSecondary,
                                        }}
                                      >
                                        {cert.certification_provider && <span>{cert.certification_provider}</span>}
                                        {cert.certification_provider && (cert.issue_date || cert.expiry_date) && (
                                          <span>·</span>
                                        )}
                                        {cert.issue_date && <span>Issue: {formatDate(cert.issue_date)}</span>}
                                        {cert.issue_date && cert.expiry_date && <span>·</span>}
                                        {cert.expiry_date && <span>Expiry: {formatDate(cert.expiry_date)}</span>}
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: SEARCE_COLORS.textSecondary,
                                    textAlign: 'center',
                                    py: 2,
                                  }}
                                >
                                  No certifications found for this employee.
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={Math.max(1, visibleColumns.length)}
                  sx={{ textAlign: 'center', py: 6, color: SEARCE_COLORS.textSecondary }}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
            {trainingFooterTotals != null && (
              <MuiTableTotalsFooterRow
                columns={visibleColumns.map((c) => ({
                  id: c.id,
                  align: c.id === 'employee_email' ? 'left' : 'center',
                }))}
                totals={trainingFooterTotals}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={sortedAndFilteredTrainingSummary.length}
        rowsPerPage={trainingSummaryRowsPerPage}
        page={trainingSummaryPage}
        onPageChange={(_, newPage) => setTrainingSummaryPage(newPage)}
        onRowsPerPageChange={(e) => {
          setTrainingSummaryRowsPerPage(parseInt(e.target.value, 10));
          setTrainingSummaryPage(0);
        }}
        sx={{
          mb: 4,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: SEARCE_COLORS.textSecondary,
            fontWeight: 500,
          },
          '& .MuiTablePagination-select': {
            color: SEARCE_COLORS.primary,
            fontWeight: 600,
          },
          '& .MuiIconButton-root': {
            color: SEARCE_COLORS.primary,
            '&:hover': {
              backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
            },
          },
        }}
      />
    </>
  );
};

export default EmployeeTrainingsSummaryTable;
